import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import PopupOverlay from "../Background/PopupOverlay";
import Button from "../../atoms/Button";
import "./ChannelCreatorModal.scss";

export default function ChannelCreatorModal({
    isOpen,
    draft,
    isSubmitting,
    error,
    onChange,
    onClose,
    onSubmit,
    groupName,
}) {
    const { t } = useTranslation();
    if (!isOpen) {
        return null;
    }

    const nameLength = (draft?.name || "").length;
    const maxName = 80;
    const nameTooLong = nameLength > maxName;
    const nameEmpty = (draft?.name || "").trim().length === 0;
    const isCreateDisabled = isSubmitting || nameEmpty || nameTooLong;
    const workspaceName = groupName || t("bandsPage.channelCreator.header.workspaceFallback");
    const workspaceVisibilityName = groupName || t("bandsPage.channelCreator.visibility.workspaceFallback");
    const channelTypes = [
        { value: "OPEN", label: t("bandsPage.channelCreator.fields.type.options.open") },
        { value: "PRIVATE", label: t("bandsPage.channelCreator.fields.type.options.private") },
        { value: "ANNOUNCEMENT", label: t("bandsPage.channelCreator.fields.type.options.announcement") },
        { value: "AUDIO", label: t("bandsPage.channelCreator.fields.type.options.audio") },
    ];

    return ReactDOM.createPortal(
        <PopupOverlay onClose={onClose} closeOnOverlayClick closeOnEsc>
            <div className="channel-creator sheet" role="dialog" aria-modal="true" aria-labelledby="channelCreatorTitle">
                <header className="channel-creator__header">
                    <button
                        type="button"
                        className="channel-creator__back"
                        onClick={onClose}
                        aria-label={t("bandsPage.channelCreator.header.back")}
                    >
                        ← {t("bandsPage.channelCreator.header.back")}
                    </button>
                    <div className="channel-creator__titles">
                        <h2 id="channelCreatorTitle">{t("bandsPage.channelCreator.header.title")}</h2>
                        <p className="channel-creator__subtitle">
                            {t("bandsPage.channelCreator.header.subtitle", { name: workspaceName })}
                        </p>
                    </div>
                </header>
                <form className="channel-creator__form" onSubmit={onSubmit}>
                    <div className="form-block">
                        <label className="label">{t("bandsPage.channelCreator.fields.name.label")}</label>
                        <div className={`channel-input ${nameTooLong ? 'has-error' : ''}`}>
                            <span className="channel-input__prefix" aria-hidden>#</span>
                            <input
                                className="channel-input__field"
                                value={draft.name}
                                onChange={onChange("name")}
                                placeholder={t("bandsPage.channelCreator.fields.name.placeholder")}
                                maxLength={maxName}
                                required
                                aria-invalid={nameTooLong}
                            />
                            <span className="channel-input__counter">{maxName - nameLength}</span>
                        </div>
                        <p className="helper">{t("bandsPage.channelCreator.fields.name.helper")}</p>
                    </div>

                    <div className="form-block">
                        <label className="label">{t("bandsPage.channelCreator.visibility.label")}</label>
                        <div className="visibility-options">
                            <label className="radio">
                                <input type="radio" name="visibility" checked={!draft.privateChannel} onChange={() => onChange("privateChannel")({ target: { checked: false } })} />
                                <div>
                                    <span className="radio__title">
                                        {t("bandsPage.channelCreator.visibility.publicTitle", { name: workspaceVisibilityName })}
                                    </span>
                                    <span className="radio__hint">{t("bandsPage.channelCreator.visibility.publicHint")}</span>
                                </div>
                            </label>
                            <label className="radio">
                                <input type="radio" name="visibility" checked={Boolean(draft.privateChannel)} onChange={() => onChange("privateChannel")({ target: { checked: true } })} />
                                <div>
                                    <span className="radio__title">{t("bandsPage.channelCreator.visibility.privateTitle")}</span>
                                    <span className="radio__hint">{t("bandsPage.channelCreator.visibility.privateHint")}</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="form-grid">
                        <label className="field">
                            <span className="label">{t("bandsPage.channelCreator.fields.slug.label")}</span>
                            <input
                                value={draft.slug}
                                onChange={onChange("slug")}
                                placeholder={t("bandsPage.channelCreator.fields.slug.placeholder")}
                                maxLength={80}
                                required
                            />
                        </label>
                        <label className="field">
                            <span className="label">{t("bandsPage.channelCreator.fields.type.label")}</span>
                            <select value={draft.type} onChange={onChange("type")}>
                                {channelTypes.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <label className="field">
                        <span className="label">{t("bandsPage.channelCreator.fields.description.label")}</span>
                        <textarea
                            value={draft.description}
                            onChange={onChange("description")}
                            placeholder={t("bandsPage.channelCreator.fields.description.placeholder")}
                            maxLength={280}
                        />
                    </label>
                    {error && <p className="channel-creator__error" role="alert">{error}</p>}
                    {nameTooLong && (
                        <p className="channel-creator__error" role="alert">
                            {t("bandsPage.channelCreator.validation.nameTooLong", { max: maxName })}
                        </p>
                    )}
                    <div className="channel-creator__actions">
                        <Button text={t("bandsPage.channelCreator.actions.cancel")} variant="secondary" onClick={onClose} type="button" />
                        <Button
                            text={isSubmitting ? t("bandsPage.channelCreator.actions.creating") : t("bandsPage.channelCreator.actions.create")}
                            variant="primary"
                            type="submit"
                            disabled={isCreateDisabled}
                        />
                    </div>
                </form>
            </div>
        </PopupOverlay>,
        document.getElementById("portal")
    );
}
